import { EverydayGuideScreen } from './everyday-guide/EverydayGuideScreen';
import type { EverydayGuide } from '../domain/recommend/everydayGuide';
import { PaymentSafetyScreen } from './payment-safety/PaymentSafetyScreen';
import type { PaymentSafetyCardView, RecurringChargeSafetyView } from './payment-safety/types';
import {
  QuickPurchaseScreen,
  type QuickPurchaseCardOption,
  type QuickPurchaseConfirmArgs,
} from './quick-purchase';
import {
  StateRefreshScreen,
  type StateRefreshCardView,
  type StateRefreshSubmitPayload,
} from './state-refresh';
import { WeeklyReviewScreen } from './weekly-review';
import { MonthlyCalendarScreen } from './monthly-calendar';
import { EvidenceDashboardScreen, type EvidenceDashboardCardView } from './evidence-dashboard';
import { PurchaseHistoryScreen, type PurchaseHistoryRow } from './purchase-history';
import { SyncStatus, type SyncStatusProps } from './sync';
import type { PurchaseQueryInput, RankCardsInput, SpendCategory } from '../domain/recommend/types';
import type { Cents } from '../domain/money';
import { asCents } from '../domain/money';
import { businessDate, type BusinessDate } from '../domain/time';
import type { CheckingReserve } from '../domain/checkingReserve';
import { computeCheckingReserve } from '../domain/checkingReserve';
import type { WeeklyReview, WeeklyPlanPayment } from '../domain/weeklyReview';
import { buildWeeklyReview } from '../domain/weeklyReview';
import type { MonthlyCalendar } from '../domain/monthlyCalendar';
import { buildMonthlyCalendar } from '../domain/monthlyCalendar';

export type AppProps = {
  /** Portfolio rows for the payment safety screen. Empty until cards are loaded. */
  safetyCards?: readonly PaymentSafetyCardView[];
  /** Cards for Quick Purchase confirm override. */
  purchaseCards?: readonly QuickPurchaseCardOption[];
  /** Build RankCardsInput from the query — local snapshot only. */
  buildRankInput?: (query: PurchaseQueryInput) => RankCardsInput;
  /** Persist confirm locally (Dexie + outbox). */
  onConfirmPurchase?: (args: QuickPurchaseConfirmArgs) => Promise<void>;
  protectionPromptThresholdCents?: Cents;
  /**
   * Everyday Guide (Task 8) — secondary reference, not home.
   * Null until a portfolio snapshot has been ranked into amount bands.
   */
  everydayGuide?: EverydayGuide | null;
  /** Authoritative state refresh (Task 12) — previous values + deltas for all cards. */
  refreshCards?: readonly StateRefreshCardView[];
  /** Persist new financial observations (append-only). */
  onSubmitRefresh?: (payload: StateRefreshSubmitPayload) => Promise<void>;
  /** Weekly review + checking reserve (Task 13 / docs/04 §5–§7). */
  weeklyReview?: WeeklyReview | null;
  checkingReserve?: CheckingReserve | null;
  onPlanWeeklyPayment?: (payload: WeeklyPlanPayment) => Promise<void>;
  onUpdateCheckingBalance?: (args: {
    checking_balance_cents: Cents;
    checking_balance_updated_at: BusinessDate;
  }) => Promise<void>;
  /** Monthly calendar (Task 13 / docs/04 §6). */
  monthlyCalendar?: MonthlyCalendar | null;
  /** Evidence dashboard (Task 20) — read-only fields + cap usage. */
  evidenceCards?: readonly EvidenceDashboardCardView[];
  /** Purchase history (Task 21) — category correction + recurring flag. */
  purchaseHistory?: readonly PurchaseHistoryRow[];
  onCorrectCategory?: (args: {
    transaction_id: string;
    corrected_category: SpendCategory;
  }) => Promise<void>;
  onFlagRecurring?: (args: { transaction_id: string }) => Promise<void>;
  /** Flagged recurring charges on payment safety (Task 21). */
  recurringCharges?: readonly RecurringChargeSafetyView[];
  /** Task 16 — outbox push status (pending count + permanent failures). */
  syncStatus?: SyncStatusProps | null;
};

const EMPTY_AS_OF = businessDate('2025-01-01');

function emptyWeeklyReview(): WeeklyReview {
  return buildWeeklyReview({
    asOf: EMPTY_AS_OF,
    cards: [],
    transactions: [],
    payments: [],
    reservations: [],
  });
}

function emptyCheckingReserve(): CheckingReserve {
  return computeCheckingReserve({
    asOf: EMPTY_AS_OF,
    checking_balance_cents: null,
    checking_balance_updated_at: null,
    reservations: [],
  });
}

function emptyMonthlyCalendar(): MonthlyCalendar {
  return buildMonthlyCalendar({
    asOf: EMPTY_AS_OF,
    cards: [],
    cycles: [],
    payments: [],
    transactions: [],
    allocations: [],
  });
}

function emptyRankInput(query: PurchaseQueryInput): RankCardsInput {
  const d = new Date();
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return {
    query,
    merchant: null,
    cards: [],
    rewardRules: [],
    offers: [],
    benefits: [],
    capPeriods: [],
    categoryObservations: [],
    settings: {
      utilization_warning_bps: 3000,
      utilization_hard_bps: 8000,
      require_active_grace_for_spending: true,
      allow_interest_bearing_recommendations: false,
      treat_likely_active_as_eligible: false,
      min_confidence_to_recommend: 'user_reported',
      assumed_worst_apr_bps: 3000,
      assume_pay_before_close: false,
      financial_staleness_days: 7,
    },
    asOf: businessDate(`${String(y)}-${m}-${day}`),
  };
}

function noopConfirm(_args: QuickPurchaseConfirmArgs): Promise<void> {
  void _args;
  return Promise.resolve();
}

function noopRefresh(_payload: StateRefreshSubmitPayload): Promise<void> {
  void _payload;
  return Promise.resolve();
}

function noopCorrectCategory(_args: {
  transaction_id: string;
  corrected_category: SpendCategory;
}): Promise<void> {
  void _args;
  return Promise.resolve();
}

function noopFlagRecurring(_args: { transaction_id: string }): Promise<void> {
  void _args;
  return Promise.resolve();
}

export function App({
  safetyCards = [],
  purchaseCards = [],
  buildRankInput = emptyRankInput,
  onConfirmPurchase = noopConfirm,
  protectionPromptThresholdCents = asCents(30_000n),
  everydayGuide = null,
  refreshCards = [],
  onSubmitRefresh = noopRefresh,
  weeklyReview = null,
  checkingReserve = null,
  onPlanWeeklyPayment,
  onUpdateCheckingBalance,
  monthlyCalendar = null,
  evidenceCards = [],
  purchaseHistory = [],
  onCorrectCategory = noopCorrectCategory,
  onFlagRecurring = noopFlagRecurring,
  recurringCharges = [],
  syncStatus = null,
}: AppProps): JSX.Element {
  const review = weeklyReview ?? emptyWeeklyReview();
  const checking = checkingReserve ?? emptyCheckingReserve();
  const calendar = monthlyCalendar ?? emptyMonthlyCalendar();

  return (
    <main
      data-testid="app-shell"
      className="mx-auto flex min-h-dvh max-w-md flex-col gap-6 px-5 py-10"
    >
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">CardZapp</h1>
        <p className="mt-1 text-sm text-slate-400">
          Type a purchase → best card, offline → confirm the exact total.
        </p>
      </header>

      {syncStatus !== null ? <SyncStatus {...syncStatus} /> : null}

      <QuickPurchaseScreen
        cards={purchaseCards}
        buildRankInput={buildRankInput}
        protectionPromptThresholdCents={protectionPromptThresholdCents}
        onConfirm={onConfirmPurchase}
      />

      <PaymentSafetyScreen cards={safetyCards} recurringCharges={recurringCharges} />

      {safetyCards.length === 0 ? (
        <section className="rounded-lg border border-slate-800 bg-slate-900/60 p-4">
          <h2 className="text-sm font-medium text-slate-300">Not currently eligible</h2>
          <p className="mt-2 text-sm text-slate-400">
            No card is spend-eligible until its grace policy is verified from an issuer document.
            This screen is a primary surface, not an edge case.
          </p>
        </section>
      ) : null}

      <PurchaseHistoryScreen
        rows={purchaseHistory}
        onCorrectCategory={onCorrectCategory}
        onFlagRecurring={onFlagRecurring}
      />

      <StateRefreshScreen cards={refreshCards} onSubmit={onSubmitRefresh} />

      <WeeklyReviewScreen
        review={review}
        checking={checking}
        onPlanPayment={onPlanWeeklyPayment}
        onUpdateCheckingBalance={onUpdateCheckingBalance}
      />

      <MonthlyCalendarScreen calendar={calendar} />

      <EvidenceDashboardScreen cards={evidenceCards} />

      <EverydayGuideScreen guide={everydayGuide} />
    </main>
  );
}
